import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/AddOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import LineItemsDialog from '@/components/common/LineItemsDialog';
import SearchableSelect from '@/components/common/SearchableSelect';
import StatusChip from '@/components/common/StatusChip';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import { PERMISSIONS } from '@/types/auth';
import type { WarehouseDto } from '@/types/organization';
import type { ClientDto, MaterialDto, PagedResult, SupplierDto } from '@/types/materials';
import type { GateEntryDto, PurchaseOrderDto } from '@/types/purchasing';
import type { CreateGrnRequest, GrnDto, GrnLineDto, GrnLineInput, UpdateGrnRequest } from '@/types/grn';

const emptyLine: GrnLineInput = {
  materialId: '', purchaseOrderLineId: null, gateEntryLineId: null, orderedQty: 0, receivedQty: 0, acceptedQty: 0,
  rejectedQty: 0, batchNo: null, mfgDate: null, expiryDate: null, unitPrice: 0,
};

export default function GrnPage() {
  const { t } = useTranslation('grn');
  const { formatDate } = useDateFormat();
  const queryClient = useQueryClient();
  const { data: config } = useProcessConfiguration();

  const { data: grns = [], isLoading } = useQuery({
    queryKey: ['grns'],
    queryFn: async () => (await apiClient.get<GrnDto[]>('/goods-receipt-notes')).data,
  });
  const { data: warehouses = [] } = useQuery({
    queryKey: ['warehouses'],
    queryFn: async () => (await apiClient.get<WarehouseDto[]>('/warehouses')).data,
  });
  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers'],
    queryFn: async () => (await apiClient.get<SupplierDto[]>('/suppliers')).data,
  });
  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => (await apiClient.get<ClientDto[]>('/clients')).data,
    enabled: config?.thirdPartyLogisticsEnabled ?? false,
  });
  const { data: purchaseOrders = [] } = useQuery({
    queryKey: ['purchase-orders'],
    queryFn: async () => (await apiClient.get<PurchaseOrderDto[]>('/purchase-orders')).data,
    enabled: config?.purchaseOrderEnabled ?? false,
  });
  const { data: gateEntries = [] } = useQuery({
    queryKey: ['gate-entries'],
    queryFn: async () => (await apiClient.get<GateEntryDto[]>('/gate-entries')).data,
    enabled: config?.gateEntryEnabled ?? false,
  });
  // Same "not yet fully received, not manually closed" availability rule as Gate Entry's own PO
  // dropdown — once a PO/Gate Entry is fully received or closed, it's done and stops offering
  // itself. The explicit line-balance check (rather than relying on status alone) matches
  // applyPurchaseOrder's own filter, so a PO/Gate Entry disappears the moment nothing is left.
  const pendingPurchaseOrders = purchaseOrders.filter(
    (po) => (po.status === 'Approved' || po.status === 'PartiallyReceived') && !po.isClosed && po.lines.some((l) => l.orderedQty - l.receivedQty > 0),
  );
  const openGateEntries = gateEntries.filter((ge) => ge.status !== 'Exited' && !ge.isFullyReceived && !ge.isClosed);

  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState<GrnDto | null>(null);
  const [editing, setEditing] = useState<GrnDto | null>(null);
  const [editVehicleNo, setEditVehicleNo] = useState('');
  const [editInwardDocNo, setEditInwardDocNo] = useState('');
  const [purchaseOrderId, setPurchaseOrderId] = useState<string>('');
  const [gateEntryId, setGateEntryId] = useState<string>('');
  const [supplierId, setSupplierId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [clientId, setClientId] = useState('');
  const [vehicleNo, setVehicleNo] = useState('');
  const [inwardDocNo, setInwardDocNo] = useState('');
  const [lines, setLines] = useState<GrnLineInput[]>([{ ...emptyLine }]);

  // Once 3PL is on, materials are scoped to the selected Client — the picker stays empty until one
  // is chosen (mirrors PutawayPage's queryKey-includes-upstream-id + enabled: Boolean(...) convention).
  const { data: materialsPage } = useQuery({
    queryKey: ['materials', 'all-for-picker', config?.thirdPartyLogisticsEnabled ? clientId : null],
    queryFn: async () =>
      (
        await apiClient.get<PagedResult<MaterialDto>>('/materials', {
          params: { page: 1, pageSize: 200, clientId: config?.thirdPartyLogisticsEnabled ? clientId || undefined : undefined },
        })
      ).data,
    enabled: !config?.thirdPartyLogisticsEnabled || Boolean(clientId),
  });
  const materials = materialsPage?.items ?? [];

  const resetForm = () => {
    setPurchaseOrderId('');
    setGateEntryId('');
    setSupplierId('');
    setWarehouseId('');
    setClientId('');
    setVehicleNo('');
    setInwardDocNo('');
    setLines([{ ...emptyLine }]);
  };

  // Only the still-unreceived balance is offered — a PO line already fully received elsewhere is
  // dropped entirely rather than added at qty 0. When Gate Entry is enabled and this PO already
  // has an open (not fully-received/closed/exited) Gate Entry logged against it, that Gate Entry
  // is the more authoritative source — auto-select it and populate lines the same way
  // applyGateEntry does, instead of going straight off the PO.
  const applyPurchaseOrder = (poId: string) => {
    setPurchaseOrderId(poId);

    const linkedGateEntry = config?.gateEntryEnabled ? openGateEntries.find((ge) => ge.purchaseOrderId === poId) : undefined;
    if (linkedGateEntry) {
      applyGateEntry(linkedGateEntry.id);
      return;
    }

    setGateEntryId('');
    const po = purchaseOrders.find((p) => p.id === poId);
    if (!po) return;
    setSupplierId(po.supplierId);
    setWarehouseId(po.warehouseId);
    const balanceLines = po.lines
      .filter((l) => l.orderedQty - l.receivedQty > 0)
      .map((l) => ({
        materialId: l.materialId, purchaseOrderLineId: l.id, gateEntryLineId: null, orderedQty: l.orderedQty,
        receivedQty: l.orderedQty - l.receivedQty, acceptedQty: l.orderedQty - l.receivedQty,
        rejectedQty: 0, batchNo: null, mfgDate: null, expiryDate: null, unitPrice: l.unitPrice,
      }));
    setLines(balanceLines.length > 0 ? balanceLines : [{ ...emptyLine }]);
  };

  // Selecting a Gate Entry (optional) copies its logged items in as editable reconciliation lines.
  // The gate entry doesn't carry supplier/warehouse itself, so those are derived from its linked PO
  // when present, and its vehicle number / linked-PO id are carried onto the GRN. Same "unreceived
  // balance only" rule as applyPurchaseOrder — a gate-entry line already fully received is dropped
  // rather than added at qty 0.
  const applyGateEntry = (geId: string) => {
    setGateEntryId(geId);
    const ge = gateEntries.find((g) => g.id === geId);
    if (!ge) return;
    setVehicleNo(ge.vehicleNo);
    setInwardDocNo(ge.inwardDocNo ?? '');
    const po = ge.purchaseOrderId ? purchaseOrders.find((p) => p.id === ge.purchaseOrderId) : undefined;
    setPurchaseOrderId(po?.id ?? '');
    if (po) {
      setSupplierId(po.supplierId);
      setWarehouseId(po.warehouseId);
    }
    const balanceLines = ge.lines
      .filter((l) => l.qty - l.receivedQty > 0)
      .map((l) => {
        const poLine = po?.lines.find((pl) => pl.materialId === l.materialId);
        const remainingQty = l.qty - l.receivedQty;
        return {
          materialId: l.materialId, purchaseOrderLineId: poLine?.id ?? null, gateEntryLineId: l.id,
          orderedQty: poLine?.orderedQty ?? l.qty, receivedQty: remainingQty, acceptedQty: remainingQty,
          rejectedQty: 0, batchNo: null, mfgDate: null, expiryDate: null, unitPrice: poLine?.unitPrice ?? 0,
        };
      });
    setLines(balanceLines.length > 0 ? balanceLines : [{ ...emptyLine }]);
  };

  const updateLine = (idx: number, patch: Partial<GrnLineInput>) =>
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  const createMutation = useMutation({
    mutationFn: () => {
      const payload: CreateGrnRequest = {
        purchaseOrderId: purchaseOrderId || null, gateEntryId: gateEntryId || null, supplierId, warehouseId,
        vehicleNo: vehicleNo || null, inwardDocNo: inwardDocNo || null, lines: lines.filter((l) => l.materialId),
        clientId: config?.thirdPartyLogisticsEnabled ? clientId || null : null,
      };
      return apiClient.post('/goods-receipt-notes', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['grns'] });
      queryClient.invalidateQueries({ queryKey: ['qc-tasks-pending'] });
      queryClient.invalidateQueries({ queryKey: ['putaway-tasks-pending'] });
      // A GRN bumps PurchaseOrderLine.ReceivedQty / GateEntryLine.ReceivedQty server-side — without
      // refetching these, the "From Purchase Order"/"From Gate Entry" pickers keep showing a now
      // fully-received PO/Gate Entry as still available until something else happens to refetch them.
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['gate-entries'] });
      setOpen(false);
    },
  });

  // Deliberately narrow — a GRN's lines/quantities can never be safely rewritten after creation
  // (QC/Putaway/Stock Ledger postings already reference them); only these two descriptive fields
  // are editable. See GrnService.UpdateAsync.
  const openEdit = (r: GrnDto) => {
    setEditing(r);
    setEditVehicleNo(r.vehicleNo ?? '');
    setEditInwardDocNo(r.inwardDocNo ?? '');
  };

  const updateMutation = useMutation({
    mutationFn: () => {
      const payload: UpdateGrnRequest = { vehicleNo: editVehicleNo || null, inwardDocNo: editInwardDocNo || null };
      return apiClient.put(`/goods-receipt-notes/${editing!.id}`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['grns'] });
      setEditing(null);
    },
  });

  const columns: DataTableColumn<GrnDto>[] = [
    { field: 'grnNumber', headerName: t('table.number') },
    { field: 'supplierName', headerName: t('table.supplier'), filterable: true },
    { field: 'warehouseName', headerName: t('table.warehouse'), filterable: true },
    { field: 'grnDate', headerName: t('table.date'), render: (r) => formatDate(r.grnDate) },
    { field: 'lines', headerName: t('table.lines'), render: (r) => r.lines.length },
    { field: 'status', headerName: t('table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    ...(config?.thirdPartyLogisticsEnabled
      ? [{ field: 'clientName', headerName: t('table.client'), render: (r: GrnDto) => r.clientName ?? '—', filterable: true } as DataTableColumn<GrnDto>]
      : []),
    {
      field: 'action', headerName: t('table.actions'), render: (r) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={() => setViewing(r)}>
            {t('view')}
          </Button>
          <IfPermitted permission={PERMISSIONS.GRN_MANAGE}>
            <Button size="small" onClick={() => openEdit(r)}>
              {t('edit')}
            </Button>
          </IfPermitted>
        </Stack>
      ),
    },
  ];

  const lineColumns: DataTableColumn<GrnLineDto>[] = [
    { field: 'materialCode', headerName: t('table.material'), render: (l) => `${l.materialCode} — ${l.materialName}` },
    { field: 'orderedQty', headerName: t('table.orderedQty') },
    { field: 'receivedQty', headerName: t('form.received') },
    { field: 'acceptedQty', headerName: t('form.accepted') },
    { field: 'rejectedQty', headerName: t('form.rejected') },
    { field: 'batchNo', headerName: t('form.batchNo'), render: (l) => l.batchNo ?? '—' },
    { field: 'expiryDate', headerName: t('table.expiryDate'), render: (l) => formatDate(l.expiryDate) },
    { field: 'unitPrice', headerName: t('form.unitPrice'), render: (l) => `₹${l.unitPrice.toLocaleString()}` },
    { field: 'qcStatus', headerName: t('table.qcStatus'), render: (l) => (l.qcStatus ? <StatusChip status={l.qcStatus} /> : '—') },
  ];

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.GRN_MANAGE}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                resetForm();
                setOpen(true);
              }}
            >
              {t('add')}
            </Button>
          </IfPermitted>
        }
      />

      <DataTable columns={columns} rows={grns} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="grn" />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('add')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {(config?.gateEntryEnabled || config?.purchaseOrderEnabled) && (
              <GridContainer columns={12}>
                {config?.gateEntryEnabled && (
                  <GridItem xs={12} md={6}>
                    <SearchableSelect
                      label={t('form.linkedGateEntry')} value={gateEntryId} onChange={applyGateEntry} fullWidth
                      options={[
                        { value: '', label: t('form.none') },
                        ...openGateEntries.map((ge) => ({ value: ge.id, label: `${ge.gateEntryNo} — ${ge.vehicleNo}` })),
                      ]}
                    />
                  </GridItem>
                )}
                {config?.purchaseOrderEnabled && (
                  <GridItem xs={12} md={6}>
                    <SearchableSelect
                      label={t('form.linkedPO')} value={purchaseOrderId} onChange={applyPurchaseOrder} fullWidth
                      options={[
                        { value: '', label: t('form.none') },
                        ...pendingPurchaseOrders.map((po) => ({ value: po.id, label: `${po.poNumber} — ${po.supplierName}` })),
                      ]}
                    />
                  </GridItem>
                )}
              </GridContainer>
            )}

            <GridContainer columns={12}>
              <GridItem xs={12} md={4}>
                <SearchableSelect
                  label={t('form.supplier')} value={supplierId} onChange={setSupplierId} fullWidth
                  options={suppliers.map((s) => ({ value: s.id, label: s.name }))}
                />
              </GridItem>
              <GridItem xs={12} md={4}>
                <SearchableSelect
                  label={t('form.warehouse')} value={warehouseId} onChange={setWarehouseId} fullWidth
                  options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
                />
              </GridItem>
              <GridItem xs={12} md={4}>
                <TextField label={t('form.vehicleNo')} value={vehicleNo} onChange={(e) => setVehicleNo(e.target.value)} fullWidth />
              </GridItem>
              {config?.thirdPartyLogisticsEnabled && (
                <GridItem xs={12} md={4}>
                  <SearchableSelect
                    label={t('form.client')} value={clientId} onChange={setClientId} fullWidth
                    options={clients.map((c) => ({ value: c.id, label: c.name }))}
                  />
                </GridItem>
              )}
            </GridContainer>

            <TextField label={t('form.inwardDocNo')} value={inwardDocNo} onChange={(e) => setInwardDocNo(e.target.value)} fullWidth />

            <Divider />
            <Typography variant="subtitle2">{t('form.reconciliation')}</Typography>

            {lines.map((line, idx) => (
              <GridContainer key={idx} columns={24} spacing={1}>
                <GridItem xs={24} md={9}>
                  <SearchableSelect
                    label={t('form.material')} value={line.materialId} size="small" fullWidth
                    onChange={(materialId) => updateLine(idx, { materialId })}
                    options={materials.map((m) => ({ value: m.id, label: `${m.code} — ${m.name}` }))}
                  />
                </GridItem>
                <GridItem xs={12} md={4}>
                  <TextField
                    type="number" label={t('form.received')} value={line.receivedQty} size="small" fullWidth
                    onChange={(e) => {
                      // Whatever physically arrived — full stop. Whether this line then goes to QC
                      // or straight to Putaway/stock is decided purely by the Material's own QC
                      // Required flag (see GrnService.CreateAsync), so Accepted/Rejected are no
                      // longer separate manual inputs here: Accepted always mirrors Received in
                      // full, and Rejected stays 0, until QC (if it runs at all) says otherwise.
                      const receivedQty = Number(e.target.value);
                      updateLine(idx, { receivedQty, acceptedQty: receivedQty, rejectedQty: 0 });
                    }}
                  />
                </GridItem>
                <GridItem xs={12} md={7}>
                  <TextField
                    label={t('form.batchNo')} value={line.batchNo ?? ''} size="small" fullWidth
                    onChange={(e) => updateLine(idx, { batchNo: e.target.value || null })}
                  />
                </GridItem>
                <GridItem xs={12} md={3}>
                  <TextField
                    type="number" label={t('form.unitPrice')} value={line.unitPrice} size="small" fullWidth
                    onChange={(e) => updateLine(idx, { unitPrice: Number(e.target.value) })}
                  />
                </GridItem>
                <GridItem xs={24} md={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-end', md: 'center' }, height: '100%' }}>
                    <IconButton size="small" onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))} disabled={lines.length <= 1}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </GridItem>
              </GridContainer>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={() => setLines((prev) => [...prev, { ...emptyLine }])} sx={{ alignSelf: 'flex-start' }}>
              {t('form.addLine')}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>{t('cancel')}</Button>
          <Button
            variant="contained"
            disabled={!supplierId || !warehouseId || lines.some((l) => !l.materialId || l.receivedQty <= 0) || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {t('form.generate')}
          </Button>
        </DialogActions>
      </Dialog>

      <LineItemsDialog
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        title={viewing?.grnNumber ?? ''}
        subtitle={viewing ? `${viewing.supplierName} — ${viewing.warehouseName}` : undefined}
        columns={lineColumns}
        rows={viewing?.lines ?? []}
      />

      <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing?.grnNumber ?? ''}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label={t('form.vehicleNo')} value={editVehicleNo} onChange={(e) => setEditVehicleNo(e.target.value)} fullWidth />
            <TextField label={t('form.inwardDocNo')} value={editInwardDocNo} onChange={(e) => setEditInwardDocNo(e.target.value)} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditing(null)}>{t('cancel')}</Button>
          <Button variant="contained" disabled={updateMutation.isPending} onClick={() => updateMutation.mutate()}>
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
